/**
 * The API client for the dashboard.
 *
 * Wraps the generated, type-checked client (`@nlocal/api-client`) with the two things the
 * SPA needs on top: Sanctum's CSRF handshake and access to the active workspace store.
 *
 * Laravel serves this SPA, so requests are same-origin: no CORS preflight, and the session
 * cookie is first-party rather than a cross-subdomain one (DECISIONS.md D-009).
 */
import { createApiClient, isApiError, type ApiError } from '@nlocal/api-client'
import { config } from '@/config'

/**
 * Origin only — no `/v1`.
 *
 * The generated `paths` keys already carry the version (`/v1/auth/login`), because that is
 * how the routes appear in the OpenAPI spec. Appending it here too produces `/v1/v1/…`,
 * which 404s at runtime while every type still checks out.
 */
const BASE_URL = config.apiUrl

/**
 * Set lazily by the workspace store.
 *
 * A plain module-level getter rather than an injected dependency: the client is created once
 * at import time, but the store only exists after Pinia is installed, and every alternative
 * (creating the client per-request, injecting on every call) is more machinery for the same
 * result.
 */
let workspaceIdResolver: () => string | null = () => null

export function setWorkspaceResolver(resolver: () => string | null): void {
  workspaceIdResolver = resolver
}

export const api = createApiClient({
  baseUrl: BASE_URL,
  workspaceId: () => workspaceIdResolver(),
})

let csrfPromise: Promise<void> | null = null

/**
 * Fetch Sanctum's CSRF cookie (§6.1).
 *
 * Half of the handshake. This puts the token in the cookie jar; echoing it back as
 * `X-XSRF-TOKEN` is the other half, and belongs to the client's own request middleware in
 * `@nlocal/api-client` — the cookie alone never satisfies Laravel, because the browser would
 * attach it to a forged request just as readily.
 *
 * Required before the first state-changing request of a session. Deduplicated: a page that
 * fires several mutations on mount would otherwise race several handshakes, and Laravel
 * rotates the token on each one — leaving all but the last request with a stale token.
 */
export function ensureCsrfCookie(): Promise<void> {
  csrfPromise ??= fetch(`${config.apiUrl}/sanctum/csrf-cookie`, {
    credentials: 'same-origin',
  })
    .then(() => undefined)
    .catch((error) => {
      // Let the next attempt retry rather than caching the failure forever.
      csrfPromise = null
      throw error
    })

  return csrfPromise
}

/** Drop the cached handshake, e.g. after logout. */
export function resetCsrf(): void {
  csrfPromise = null
}

export { isApiError }
export type { ApiError }

/** Human-readable message for any thrown or returned error. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (isApiError(error)) return error.error.message
  if (error instanceof Error) return error.message
  return fallback
}

/** The §5 error code, when there is one. Useful for switching on `TUNNEL_LIMIT_REACHED` etc. */
export function errorCode(error: unknown): string | null {
  return isApiError(error) ? error.error.code : null
}

/**
 * The docs anchor the server attached to this error.
 *
 * Taken from the envelope rather than rebuilt client-side, so the dashboard's link and the one
 * `nlocal` prints in the terminal are byte-identical — which is what §18 item 3's "shown
 * identically in CLI and UI" actually asks for.
 */
export function errorDocs(error: unknown): string | null {
  return isApiError(error) ? (error.error.docs ?? null) : null
}

/** Field-level validation errors from a 422, keyed by field name. */
export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!isApiError(error)) return {}
  const fields = error.error.details?.fields
  return typeof fields === 'object' && fields !== null ? (fields as Record<string, string[]>) : {}
}

/**
 * Hands the browser a text file without a network round trip, and cleans up after itself.
 *
 * Here rather than in one resource module because three of them export files — tunnels as YAML,
 * domains as BIND, access rules as HCL/JSON/TF — and each had been about to grow its own copy.
 */
export function downloadTextFile(filename: string, body: string, mime = 'text/plain'): void {
  const url = URL.createObjectURL(new Blob([body], { type: mime }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
